import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PurchaseOrder, purchaseOrdersAPI, suppliersAPI, Supplier } from '../../services/api';
import {
    PlusIcon,
    EyeIcon,
    TrashIcon,
    FunnelIcon,
    XMarkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Badge, Spinner, EmptyState, PageHero } from '../ui';

const STATUS_CONFIG: Record<string, { label: string; variant: 'neutral' | 'warning' | 'primary' | 'success' }> = {
    CREATED: { label: 'Created', variant: 'neutral' },
    SENT: { label: 'Sent', variant: 'warning' },
    CONFIRMED: { label: 'Confirmed', variant: 'primary' },
    RECEIVED: { label: 'Received', variant: 'success' },
};

const PurchaseOrdersList: React.FC = () => {
    const [orders, setOrders] = useState<PurchaseOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
    const [showFilters, setShowFilters] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [filters, setFilters] = useState({ search: '', status: '', supplierId: '' });
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);

    useEffect(() => {
        suppliersAPI.getAll({ limit: 100 }).then((res) => setSuppliers(res.data || [])).catch(() => { });
    }, []);

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const data = await purchaseOrdersAPI.getAll({
                page: pagination.page,
                limit: pagination.limit,
                search: filters.search || undefined,
                status: filters.status || undefined,
                supplierId: filters.supplierId ? +filters.supplierId : undefined,
            });
            setOrders(data.data);
            setPagination(data.pagination);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch purchase orders');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, filters]);

    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    useEffect(() => {
        const t = setTimeout(() => {
            setFilters(prev => ({ ...prev, search: searchInput }));
            setPagination(prev => ({ ...prev, page: 1 }));
        }, 1500);
        return () => clearTimeout(t);
    }, [searchInput]);

    const handleDelete = async (id: number) => {
        try {
            await purchaseOrdersAPI.delete(id);
            fetchOrders();
            setDeleteConfirm(null);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to delete');
        }
    };

    const handleFilterChange = (key: string, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const clearFilters = () => {
        setSearchInput('');
        setFilters({ search: '', status: '', supplierId: '' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const hasActiveFilters = filters.search || filters.status || filters.supplierId;

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8">
            <PageHero
                title="Purchase Orders"
                subtitle="Manage purchase orders and track deliveries"
                icon={<DocumentTextIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
                accentColor="purple"
            >
                <div className="mt-2">
                    <Link to="/admin/purchase-orders/new">
                        <Button variant="primary" leftIcon={<PlusIcon className="h-5 w-5" />}>New PO</Button>
                    </Link>
                </div>
            </PageHero>

            {error && (
                <Card variant="bordered" className="mt-4 border-error-200 bg-error-50">
                    <CardBody className="p-4"><div className="text-sm text-error-700">{error}</div></CardBody>
                </Card>
            )}

            {/* Filters */}
            <Card variant="elevated" className="mt-6">
                <CardBody className="p-4">
                    <div className="flex items-center justify-between">
                        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} leftIcon={<FunnelIcon className="h-4 w-4" />}>
                            Filters
                            {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-2">Active</Badge>}
                        </Button>
                        {hasActiveFilters && (
                            <button onClick={clearFilters} className="text-sm text-neutral-600 hover:text-neutral-900 flex items-center transition-colors">
                                <XMarkIcon className="h-4 w-4 mr-1" /> Clear
                            </button>
                        )}
                    </div>
                    {showFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 mt-4 border-t border-neutral-200">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Search</label>
                                <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                                    placeholder="Search PO number, notes..."
                                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Status</label>
                                <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}
                                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                                    <option value="">All Status</option>
                                    <option value="CREATED">Created</option>
                                    <option value="SENT">Sent</option>
                                    <option value="CONFIRMED">Confirmed</option>
                                    <option value="RECEIVED">Received</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Supplier</label>
                                <select value={filters.supplierId} onChange={(e) => handleFilterChange('supplierId', e.target.value)}
                                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                                    <option value="">All Suppliers</option>
                                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Table */}
            <Card variant="elevated" className="mt-4 overflow-hidden">
                {loading && orders.length === 0 ? (
                    <div className="flex justify-center items-center h-40"><Spinner size="lg" /></div>
                ) : (
                    <>
                        <table className="min-w-full divide-y divide-neutral-200">
                            <thead className="bg-neutral-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">PO Number</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Supplier</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Company</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Total</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Documents</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Expected Delivery</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-neutral-200">
                                {orders.map((po) => {
                                    const sc = STATUS_CONFIG[po.status] || STATUS_CONFIG.CREATED;
                                    return (
                                        <tr key={po.id} className="hover:bg-neutral-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <Link to={`/admin/purchase-orders/${po.id}`} className="text-sm font-medium text-primary-600 hover:text-primary-900">{po.poNumber}</Link>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{po.supplier?.name || '-'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{po.company?.name || '-'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <Badge variant={sc.variant} size="sm">{sc.label}</Badge>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                                                {po.currency} {Number(po.totalAmount).toFixed(2)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                                                {po._count?.documents ?? po.documents?.length ?? 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                                                {po.expectedDeliveryDate ? new Date(po.expectedDeliveryDate).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-2">
                                                    <Link to={`/admin/purchase-orders/${po.id}`} className="text-primary-600 hover:text-primary-900"><EyeIcon className="h-5 w-5" /></Link>
                                                    {deleteConfirm === po.id ? (
                                                        <div className="flex space-x-2">
                                                            <button onClick={() => handleDelete(po.id)} className="text-error-600 hover:text-error-900 font-medium">Confirm</button>
                                                            <button onClick={() => setDeleteConfirm(null)} className="text-neutral-600 hover:text-neutral-900">Cancel</button>
                                                        </div>
                                                    ) : (
                                                        <button onClick={() => setDeleteConfirm(po.id)} className="text-error-600 hover:text-error-900"><TrashIcon className="h-5 w-5" /></button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {orders.length === 0 && !loading && (
                            <EmptyState icon={<DocumentTextIcon className="h-12 w-12" />} title="No purchase orders" description="Create your first purchase order to get started." />
                        )}
                    </>
                )}
            </Card>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <Card variant="elevated" className="mt-4">
                    <CardBody className="px-4 py-3">
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <p className="text-sm text-neutral-700">
                                Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                                <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>{' '}
                                of <span className="font-medium">{pagination.total}</span>
                            </p>
                            <div className="flex items-center space-x-2">
                                <Button variant="outline" size="sm"
                                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                                    disabled={pagination.page === 1}><ChevronLeftIcon className="h-5 w-5" /></Button>
                                <span className="text-sm text-neutral-700">Page {pagination.page} of {pagination.pages}</span>
                                <Button variant="outline" size="sm"
                                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                                    disabled={pagination.page === pagination.pages}><ChevronRightIcon className="h-5 w-5" /></Button>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            )}
        </div>
    );
};

export default PurchaseOrdersList;
